import type { Block, Cell, Column, TableBlock, Tone } from '@/kova/spec/types'

import type { ApiClient } from '@/lib/api'
import { ApiError } from '@/lib/api/errors'

import { customerProfilePath } from './customer'
import {
  decideBlock, parseReviewKey, type Caller,
} from './handles'
import type { DataSource, ScreenData } from './provenance'
import { readAtStamp } from './provenance'

/**
 * The handle claim journal — every name a customer attempted and what they were
 * told, refusals included (design brief §6.18.3, go-wallet#717).
 *
 * # The question this answers, and what answering it used to cost
 *
 * "Why was I not allowed that name." `handle_claims` takes a row on EVERY
 * outcome including a refusal, because a table holding only successes cannot
 * answer it — and until #717 the row was written by storage, read by nothing,
 * and reachable only from a SQL prompt against production. This is the read of
 * it, and it is what turns a support conversation from a guess into a citation.
 *
 * # It lives on Customer 360, not in the Handles section
 *
 * A refusal is something you look up about **a person**. The route is
 * per-customer and requires `customer.read` — the CUSTOMER permission, not
 * `handle.read` — because the journal is per-customer evidence and belongs to
 * the 360. There is no left-rail destination for it and there must not be one:
 * there is no route that lists claims across customers.
 *
 * # `rule_snapshot` is the blocklist and it is operator-only
 *
 * It is every reserved term consulted, by id and version. It is registered on
 * the admin router alone and deliberately absent from the customer surface,
 * because handing it to the people it constrains tells them exactly which
 * spellings get through. So there is **no "send this to the customer" action, no
 * share link, and no copy-the-whole-snapshot affordance** anywhere in this file.
 *
 * # No write of any kind
 *
 * There is no route in this console that sets, changes, releases or retires a
 * customer's handle — not here, not on Customer 360, not anywhere. `handle_claims`
 * is append-only under a row-level trigger (law 5) and the backend read hangs off
 * a reader interface with no mutating method. The one handle-shaped act an
 * operator can take is the decision on an already-held name, and that is
 * §6.18.1's, not this surface's.
 */

// ---------------------------------------------------------------------------
// The endpoint
// ---------------------------------------------------------------------------

export function handleClaimsPath(customerID: number): string {
  return `${customerProfilePath(customerID)}/handle-claims`
}

const CLAIMS_SOURCE =
  'postgres · handle_claims (GET /v1/admin/customers/{id}/handle-claims)'

// ---------------------------------------------------------------------------
// Reading the wire, fail-closed
// ---------------------------------------------------------------------------

function text(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function exactInteger(value: unknown): number | null {
  return typeof value === 'number' && Number.isSafeInteger(value) ? value : null
}

function stamp(iso: string): string {
  if (iso === '') return ''
  const at = new Date(iso)
  return Number.isNaN(at.getTime()) ? iso : readAtStamp(at)
}

/**
 * One claim, as `handleClaimView` serialises it.
 *
 * Note the wire-shape trap: the OpenAPI marks `normalized`, `skeleton`,
 * `refusal_code` and `handle_id` `nullable: true`, but the Go structs are `*T`
 * with `,omitempty` — so on the wire they are **absent**, not `null`. Every
 * reader below narrows for both, because a console written against the schema
 * and deployed against the handler is the shape of the bug that only appears in
 * production.
 */
export interface HandleClaimView {
  readonly id: number
  /** What the customer typed. **The empty string is a real value.** */
  readonly requested: string
  readonly normalized?: string | null
  readonly skeleton?: string | null
  /** `claimed | held_for_review | refused` — a third closed set. */
  readonly outcome: string
  /** Present exactly when refused. One of fifteen machine reasons. */
  readonly refusal_code?: string | null
  /** Present exactly when a grant created a row. */
  readonly handle_id?: number | null
  readonly policy_id: number
  readonly policy_version: number
  readonly rule_snapshot: unknown
  readonly input_snapshot: unknown
  readonly actor: string
  readonly decided_at: string
  readonly created_at: string
}

export interface PageView {
  readonly limit?: unknown
  readonly returned?: unknown
  readonly has_more?: unknown
  readonly next_cursor?: unknown
  readonly paging?: unknown
  readonly paging_reason?: unknown
}

export interface HandleClaimsResponse {
  readonly generated_at?: unknown
  readonly page?: unknown
  readonly empty_note?: unknown
  readonly claims: readonly unknown[]
}

export interface ClaimRow {
  readonly id: number | null
  /** `''` is ambiguous on the wire, so absence is tracked separately. */
  readonly requested: string
  readonly requestedPresent: boolean
  readonly normalized: string | null
  readonly skeleton: string | null
  readonly outcome: string
  readonly refusalCode: string | null
  readonly handleId: number | null
  readonly policyId: number | null
  readonly policyVersion: number | null
  readonly ruleSnapshot: unknown
  readonly hasRuleSnapshot: boolean
  readonly inputSnapshot: unknown
  readonly hasInputSnapshot: boolean
  readonly actor: string
  readonly decidedAt: string
  readonly createdAt: string
}

/** A nullable string field: absent, `null` and a non-string all read as `null`. */
function optionalText(value: unknown): string | null {
  return typeof value === 'string' ? value : null
}

function readClaim(raw: unknown): ClaimRow | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null
  const c = raw as Partial<HandleClaimView>
  const snapshotPresent = (v: unknown) => v !== undefined && v !== null
  return {
    id: exactInteger(c.id),
    requested: text(c.requested),
    // `""` is a real answer — a client posting before the customer typed gets a
    // `blank` refusal and the journal records it — so "they typed nothing" and
    // "the field did not arrive" must not collapse into the same cell.
    requestedPresent: typeof c.requested === 'string',
    normalized: optionalText(c.normalized),
    skeleton: optionalText(c.skeleton),
    outcome: text(c.outcome),
    refusalCode: optionalText(c.refusal_code),
    handleId: exactInteger(c.handle_id),
    policyId: exactInteger(c.policy_id),
    policyVersion: exactInteger(c.policy_version),
    ruleSnapshot: c.rule_snapshot,
    hasRuleSnapshot: snapshotPresent(c.rule_snapshot),
    inputSnapshot: c.input_snapshot,
    hasInputSnapshot: snapshotPresent(c.input_snapshot),
    actor: text(c.actor),
    decidedAt: text(c.decided_at),
    createdAt: text(c.created_at),
  }
}

// ---------------------------------------------------------------------------
// The three closed vocabularies this screen renders
// ---------------------------------------------------------------------------

/**
 * `claimed | held_for_review | refused`.
 *
 * A third closed set, distinct from a review's `allowed | refused` and distinct
 * again from law 14's four risk outcomes. `held_for_review` spells out its cost
 * because the word "review" alone reads as a process running quietly in the
 * background, and what it means is that the name is taken against everybody else
 * and resolves to nobody in the meantime.
 */
export const OUTCOME_LABEL: Readonly<Record<string, string>> = {
  claimed: 'claimed · the name is theirs',
  held_for_review: 'held · resolves to nobody while it waits',
  refused: 'refused',
}

export const OUTCOME_TONE: Readonly<Record<string, Tone>> = {
  claimed: 'n',
  held_for_review: 'a',
  refused: 'n',
}

/**
 * The console's own plain-language reading of each refusal code.
 *
 * **This is never presented as the message the customer received.** That
 * sentence belongs to the copy catalog and changes with it; the code is what an
 * operator greps for six months later. So the code is rendered in the mono
 * identifier face and this gloss sits beside it, labelled as ours.
 *
 * Fifteen, matching the enum. A code outside them renders verbatim and says the
 * console does not know it, rather than being quietly dropped.
 *
 * Exported, like the two maps above, so the test that binds each of these
 * sentences to the code beside it is driven off this map rather than off a list
 * copied out of it. Two glosses exchanged here — `too_short` for `too_long`,
 * `iban_shaped` for `phone_shaped` — change nothing structural and every count
 * still agrees; what changes is that an operator is told the wrong reason a
 * customer's name was refused, in the one screen that exists to answer that.
 */
export const REFUSAL_GLOSS: Readonly<Record<string, string>> = {
  blank: 'nothing was typed',
  too_short: 'shorter than the policy allows',
  too_long: 'longer than the policy allows',
  edge_hyphen: 'starts or ends with a hyphen',
  double_hyphen: 'two hyphens in a row',
  charset: 'characters outside the handle vocabulary',
  iban_shaped: 'reads as an IBAN',
  all_digits: 'digits only',
  phone_shaped: 'reads as a phone number',
  reserved: 'the fold matches a reserved term published with action: refuse',
  already_yours: 'this customer already holds it',
  review_pending: 'a claim on this name is already held for review',
  cooldown: 'released too recently — measured against the incumbent it displaced',
  taken: 'somebody holds it, or holds a name that folds onto it',
  reserved_pre_launch: 'kept back before launch',
}

function outcomeChip(c: ClaimRow): Cell {
  if (c.outcome === '') return ['outcome not returned', 'r']
  if (!Object.prototype.hasOwnProperty.call(OUTCOME_TONE, c.outcome)) {
    return [`${c.outcome} · unknown to this console`, 'r']
  }
  return [OUTCOME_LABEL[c.outcome] ?? c.outcome, OUTCOME_TONE[c.outcome] ?? 'r']
}

/**
 * The refusal cell: the code, then the console's gloss, never the gloss alone.
 *
 * A code present on a non-refusal, or absent from a refusal, is a contradiction
 * with the schema (`present exactly when the outcome is refused`) and is stated
 * rather than smoothed over — it is the field a whole dispute can turn on.
 */
function refusalCell(c: ClaimRow): string {
  if (c.refusalCode === null) {
    return c.outcome === 'refused'
      ? '— refused with no code, which the schema says cannot happen'
      : '—'
  }
  const gloss = REFUSAL_GLOSS[c.refusalCode] ?? 'not a code this console knows'
  const head = `${c.refusalCode} — ${gloss}`
  return c.outcome === 'refused'
    ? head
    : `${head} · carried on a ${c.outcome === '' ? '(missing)' : c.outcome} outcome, which the schema says cannot happen`
}

/**
 * What the customer typed, with the empty string kept as an answer.
 *
 * Rendered as `@name` when there is one, quoted when it is empty. The quoting
 * matters: `""` typed into a handle box produces a `blank` refusal that the
 * journal records, and a blank cell would read as "this row lost a field".
 */
function requestedCell(c: ClaimRow): string {
  if (!c.requestedPresent) return '— not returned'
  return c.requested === '' ? '"" — nothing was typed, and that is a real attempt' : `@${c.requested}`
}

/**
 * The canonical form and its fold, which travel together.
 *
 * Both absent means the vocabulary refused the input before either could be
 * computed — an answer, not a gap. One present without the other contradicts the
 * schema's guarantee and says so.
 */
function foldCell(c: ClaimRow): string {
  if (c.normalized === null && c.skeleton === null) {
    return '— not computed: refused before either could be'
  }
  if (c.normalized === null || c.skeleton === null) {
    return c.skeleton ?? `${c.normalized ?? ''} · fold missing, though the schema pairs them`
  }
  return c.normalized === c.skeleton ? c.skeleton : `${c.normalized} → ${c.skeleton}`
}

const CLAIM_COLS: readonly Column[] = [
  ['Claim id', 'm', '92px'],
  ['What they typed', 'x2', 'minmax(150px,1.2fr)'],
  ['Canonical → fold', 'm', 'minmax(150px,1.1fr)'],
  ['Outcome', 'c', '230px'],
  ['Refusal code · this console’s gloss', 'x', 'minmax(230px,1.7fr)'],
  ['Policy', 'm', '112px'],
  ['Decided', 'm', '150px'],
  ['Actor', 'x', 'minmax(120px,1fr)'],
]

function claimRow(c: ClaimRow): readonly Cell[] {
  return [
    c.id === null ? '— no id' : String(c.id),
    requestedCell(c),
    foldCell(c),
    outcomeChip(c),
    refusalCell(c),
    // The pair a support agent quotes, and invariant 9's replayability: the
    // policy id alone names a history rather than the rules that decided this.
    c.policyId === null || c.policyVersion === null
      ? '— not stated'
      : `${String(c.policyId)}@v${String(c.policyVersion)}`,
    stamp(c.decidedAt) === '' ? '— not returned' : stamp(c.decidedAt),
    c.actor === '' ? '— unattributed' : c.actor,
  ]
}

// ---------------------------------------------------------------------------
// Paging, which has no total anywhere
// ---------------------------------------------------------------------------

interface Paging {
  readonly limit: number | null
  readonly returned: number | null
  readonly hasMore: boolean | null
  readonly nextCursor: number | null
  readonly paging: string
  readonly pagingReason: string
}

function readPage(raw: unknown): Paging {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return { limit: null, returned: null, hasMore: null, nextCursor: null, paging: '', pagingReason: '' }
  }
  const p = raw as PageView
  return {
    limit: exactInteger(p.limit),
    returned: exactInteger(p.returned),
    hasMore: typeof p.has_more === 'boolean' ? p.has_more : null,
    nextCursor: exactInteger(p.next_cursor),
    paging: text(p.paging),
    pagingReason: text(p.paging_reason),
  }
}

/**
 * The footer under the journal.
 *
 * **There is no total count anywhere on this route**, and the footer never
 * invents one. A customer who has typed forty candidates in one sitting pages by
 * keyset, and the honest sentence is "this page, and whether there is another" —
 * never "40 of 120".
 */
function claimsFooter(shown: number, unreadable: number, page: Paging): string {
  const head = `${String(shown)} attempt(s) on this page, newest first`
  const more = page.hasMore === true
    ? page.nextCursor === null
      ? ' · there are older attempts, and the backend sent no cursor to reach them'
      : ` · older attempts exist; the next page starts before claim ${String(page.nextCursor)}`
    : page.hasMore === false
      ? ' · this is the whole journal'
      : ' · whether older attempts exist was not stated'
  return unreadable === 0
    ? `${head}${more}`
    : `${head}${more} · ${String(unreadable)} row(s) could not be read`
}

function claimsTable(rows: readonly ClaimRow[], unreadable: number, page: Paging): TableBlock {
  return {
    t: 'table',
    title: 'Handle claim journal',
    src: 'ledger',
    facets: false,
    cols: CLAIM_COLS,
    rows: rows.map(claimRow),
    footer: claimsFooter(rows.length, unreadable, page),
    foot2:
      'Append-only: every attempt takes a row, including every refusal. There is no total count on this route and none is shown. The refusal code is the machine reason an operator greps for — the sentence beside it is this console’s own reading and is NOT the message the customer received, which lives in the copy catalog and changes with it.',
  }
}

// ---------------------------------------------------------------------------
// The Customer 360 band
// ---------------------------------------------------------------------------

export interface BandResult {
  readonly blocks: readonly Block[]
  readonly missing: readonly string[]
}

/**
 * Whether this payload is a claim journal at all.
 *
 * Exported because `composeCustomer360`'s 404 short-circuit has to ask the same
 * question: a claim journal that answered for a customer the profile says does
 * not exist is a contradiction worth reporting, and it can only be spotted by
 * looking inside the payload.
 */
export function isHandleClaimsResponse(payload: unknown): payload is HandleClaimsResponse {
  if (typeof payload !== 'object' || payload === null) return false
  return Array.isArray((payload as Partial<HandleClaimsResponse>).claims)
}

/**
 * The band, from one read that either answered or did not.
 *
 * `attempt` is the same discriminated shape Customer 360's other four bands
 * take, so a failure here becomes a band that says so rather than an exception
 * that takes the other four down with it.
 */
export function handleClaimsBand(
  attempt: { readonly ok: true; readonly payload: unknown }
    | { readonly ok: false; readonly reason: string; readonly status: number | null
        readonly decisionRef: string | null; readonly refused: boolean },
): BandResult {
  if (!attempt.ok) {
    // Scoped to the band. The rest of Customer 360 stays usable — a support
    // agent chasing a payment does not lose the journal and the balance
    // together because one endpoint was slow.
    return {
      blocks: [{
        t: 'note',
        tone: 'r',
        kicker: 'Handles',
        title: 'This customer’s handle claim journal could not be read',
        body: `${attempt.reason} No attempt, outcome or refusal code below is known — which is different from a customer who has never attempted a handle, and different again from a customer who does not exist. The rest of this screen is unaffected.`,
        ref: attempt.decisionRef !== null
          ? `decision_ref ${attempt.decisionRef}`
          : attempt.refused
            ? 'The backend refused this read. Reading the journal needs customer.read.'
            : 'Nothing in this band was read.',
      }],
      missing: ['every handle this customer attempted, and what they were told'],
    }
  }

  if (!isHandleClaimsResponse(attempt.payload)) {
    return {
      blocks: [{
        t: 'note',
        tone: 'r',
        kicker: 'Handles',
        title: 'The handle claim journal returned a shape this console does not know',
        body: 'Nothing is rendered from it rather than partially rendered from a guess. No claims array was on the payload.',
      }],
      missing: ['every handle this customer attempted, and what they were told'],
    }
  }

  const res = attempt.payload
  const rows: ClaimRow[] = []
  let unreadable = 0
  for (const raw of res.claims) {
    const parsed = readClaim(raw)
    if (parsed === null) unreadable += 1
    else rows.push(parsed)
  }

  const page = readPage(res.page)
  const blocks: Block[] = [claimsTable(rows, unreadable, page)]
  const missing: string[] = []

  if (rows.length === 0 && unreadable === 0) {
    // The SERVER'S sentence, rendered as sent. Console copy for this would drift
    // from the distinction the backend is making, and the distinction is the
    // whole point: an id naming nobody is a 404 on this route, so an empty list
    // here is an attempt history rather than a failed lookup.
    const note = text(res.empty_note)
    blocks.push({
      t: 'note',
      tone: 'n',
      kicker: 'Handles',
      title: 'This customer has never attempted a handle',
      body: note === ''
        ? 'The backend answered with an empty journal and sent no note explaining it. An id that names nobody is a 404 on this route, so an empty list here is the attempt history and not a failed lookup — but that sentence is this console’s, because the server’s did not arrive.'
        : note,
      ref: note === ''
        ? 'empty_note was not on the payload'
        : 'The server’s own words, rendered as sent.',
    })
    if (note === '') missing.push('the server’s own note explaining an empty journal')
  }

  if (unreadable > 0) {
    blocks.push({
      t: 'note',
      tone: 'r',
      kicker: 'Handles',
      title: `${String(unreadable)} attempt(s) could not be read`,
      body: 'The backend sent entries this console could not read as claims. They are counted rather than dropped: an attempt missing from this journal is an answer a customer was given that nobody can now cite.',
    })
    missing.push(`${String(unreadable)} unreadable claim(s)`)
  }

  if (page.hasMore === true) {
    missing.push('older attempts beyond this page (keyset-paged, and there is no total anywhere)')
  }

  const noReplay = rows.filter((c) => !c.hasRuleSnapshot || !c.hasInputSnapshot).length
  if (noReplay > 0) {
    missing.push(`${String(noReplay)} claim(s) with a missing snapshot, which are therefore not replayable`)
  }

  return { blocks, missing }
}

// ---------------------------------------------------------------------------
// Opening one review's claim — the row-open on the impersonation queue
// ---------------------------------------------------------------------------

/**
 * One claim, addressed exactly, out of a per-customer keyset page.
 *
 * There is no `GET /handles/claims/{id}` — the journal is keyed by CUSTOMER —
 * so reaching the claim a review names takes the customer's journal and a
 * cursor. `before_id` returns rows with a smaller id, newest first, so
 * `before_id = claimID + 1` puts the wanted claim first and `limit = 1` stops
 * there. That is one row over the wire rather than up to two hundred, and it is
 * exact rather than a scan that gives up after a page.
 *
 * The row that comes back is still CHECKED against the id that was asked for.
 * `handle_claims` is append-only so the claim cannot have been deleted, but a
 * cursor arithmetic error would return the neighbouring claim — and rendering
 * another attempt's rule snapshot under this review would be the most convincing
 * wrong answer this view could give: every word of it read from the backend, and
 * all of it about a different name.
 */
export function claimLookupQuery(claimID: number): Readonly<Record<string, number>> {
  return { before_id: claimID + 1, limit: 1 }
}

/**
 * Reads the claim behind a held handle, and composes the decision control.
 *
 * Two reads, in parallel and independent: the claim (why the name was held) and
 * the caller (whether this session may decide). Neither can fail the other — a
 * journal this session may not read still leaves the decision drawable, and a
 * `/me` that failed still leaves the evidence readable with the control
 * disabled and the reason stated.
 *
 * The decide block renders whatever happened to the claim read. That is
 * deliberate and it is §6.18.1's third degraded state: the queue must stay
 * workable from `handle`, `customer_id` and the claim, because **the decision is
 * about the name** — and an operator who can see the held name and the queue's
 * own reason is not blocked by a customer lookup that timed out.
 */
export async function readHeldHandle(
  api: ApiClient,
  key: string,
  caller: Caller,
): Promise<ScreenData> {
  const parsed = parseReviewKey(key)
  if (parsed === null) {
    return {
      blocks: [{
        t: 'note',
        tone: 'r',
        kicker: 'Held handle',
        title: 'This row does not carry an identity this console can open',
        body: `The row key ${JSON.stringify(key)} is not a review, customer and claim triple. Nothing was read and nothing is shown — a request built from a partial identity would report on whatever answered.`,
      }],
      provenance: { kind: 'unread', source: CLAIMS_SOURCE, reason: 'The row carried no usable identity.' },
    }
  }

  let claim: ClaimRow | null = null
  let failure: string = ''
  let refused = false
  try {
    const payload = await api.get<unknown>(handleClaimsPath(parsed.customerID), {
      query: claimLookupQuery(parsed.claimID),
    })
    if (!isHandleClaimsResponse(payload)) {
      failure = 'The claim journal returned a shape this console does not know: no claims array was on it.'
    } else {
      const [first] = payload.claims
      const row = first === undefined ? null : readClaim(first)
      if (row === null) {
        failure = `The claim journal answered, and claim ${String(parsed.claimID)} was not on the page the cursor addressed.`
      } else if (row.id !== parsed.claimID) {
        failure = `The claim journal answered about claim ${row.id === null ? '(no id)' : String(row.id)}, which is not the claim this review names. Nothing from it is shown: another attempt's ruleset rendered under this name would be a wrong answer read entirely from the backend.`
      } else {
        claim = row
      }
    }
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    if (error instanceof ApiError) {
      refused = error.isForbidden
      const code = error.code === null ? '' : ` (${error.code})`
      failure = error.status === 404
        ? `The claim journal answered 404: no customer holds id ${String(parsed.customerID)}, which contradicts the queue row that named them.`
        : `The claim journal answered ${String(error.status)}${code}.`
    } else {
      failure = 'The claim journal could not be reached.'
    }
  }

  const handle = claim === null ? '' : claim.requested
  const blocks: Block[] = []
  const missing: string[] = []

  if (claim === null) {
    blocks.push({
      t: 'note',
      tone: 'a',
      kicker: 'Held handle',
      title: 'The claim behind this hold could not be read',
      body: `${failure} The queue's own one-line reason names only the first term that matched, so the full ruleset that produced this hold is not available here. The decision below is still offered — it is about the NAME, and a customer lookup that failed must not strand a creator whose handle resolves to nobody — but decide on the queue row and the audit trail rather than on evidence this console could not fetch.${refused ? ' Reading the journal needs customer.read.' : ''}`,
      ref: `Review ${String(parsed.reviewID)} · customer ${String(parsed.customerID)} · claim ${String(parsed.claimID)}`,
    })
    missing.push('the claim that produced this hold, and its rule snapshot')
  } else {
    blocks.push({
      t: 'kv',
      title: `Why @${claim.requested === '' ? '(nothing typed)' : claim.requested} is held`,
      items: [
        ['what they typed', requestedCell(claim), '', 'n',
          'Bounded to 64 characters by the column. The empty string is a real value.', 'm'],
        ['canonical → fold', foldCell(claim), '', 'n',
          'The fold is what actually matched — it is why a name nobody would read as an imitation can still collide with a reserved term.', 'm'],
        ['outcome', claim.outcome === '' ? '— not returned' : claim.outcome,
          claim.outcome === 'held_for_review' ? 'held' : '',
          claim.outcome === 'held_for_review' ? 'a' : 'r',
          claim.outcome === 'held_for_review'
            ? 'The name is taken against everybody else and resolves to nobody while it waits. That is the fail-closed direction and it is correct — and the cost of it is borne by the customer.'
            : 'The queue named this claim as the one that produced a hold, and the claim itself does not say it was held. One of the two is wrong; nothing here resolves which.',
          ''],
        ['decided', claim.decidedAt === '' ? '— not returned' : stamp(claim.decidedAt), '', 'n',
          'When the claim was answered — which is when the wait began.', 'm'],
        ['actor', claim.actor === '' ? '— unattributed' : claim.actor, '', 'n',
          'The resolved principal as journalled, never taken from a body.', ''],
      ],
    })
    if (!claim.hasRuleSnapshot || !claim.hasInputSnapshot) {
      missing.push('a snapshot on this claim, which is therefore not replayable')
    }
  }

  blocks.push(decideBlock(parsed, handle, caller, api))
  if (caller.session === null) missing.push('what this session is permitted to do')

  const readAt = readAtStamp(new Date())
  const provenance: DataSource = missing.length === 0
    ? { kind: 'live', source: CLAIMS_SOURCE, authority: 'operational', readAt }
    : { kind: 'partial', source: CLAIMS_SOURCE, authority: 'operational', readAt, missing }

  return { blocks, provenance }
}

